import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FiChevronLeft, FiChevronRight, FiUpload, FiX } from 'react-icons/fi';
import {
  createLocation,
  fetchAllLocations,
  fetchAllLocationsForHierarchy,
  fetchLocationTypes,
  fetchLocationsDropdown,
  humanizeError,
  updateLocation,
  uploadLocationsBulk,
} from '../services/api';
import ClientProjectDropdowns from '../components/filters/ClientProjectDropdowns';
import HierarchyFilterDropdowns from '../components/filters/HierarchyFilterDropdowns';
import {
  applyHierarchyFilters,
  getActiveFilterMeta,
  getFilterByDropdownOptions,
  getLocationOptionsFromRows,
} from '../constants/hierarchyFilters';
import Button from '../components/global/Button';
import DataTable from '../components/global/DataTable';
import Dropdown from '../components/global/Dropdown';
import exportIcon from '../assets/icons/ExportIcon.svg';
import { useClientProjectScope } from '../hooks/useClientProjectScope';
import { useAuth } from '../context/AuthContext';
import {
  clientListExists,
  getProjectClientId,
  getProjectId,
  getSortedClientOptions,
  getSortedProjectOptions,
} from '../utils/clientProjectSelectOptions';
import {
  buildLocationTypeMetaMap,
  enrichLocationsWithHierarchy,
  getHierarchyColumnDefs,
  getProjectLabelMap,
  sortLocationRows,
} from '../utils/locationHierarchy';
import { buildPageList } from '../utils/pagination';
import './Locations.css';
import './Dashboard.css';
import './StatusMetersPage.css';

const PAGE_SIZE = 10;
const DEFAULT_PROJECT_ID = process.env.REACT_APP_PROJECT_ID;

function resolveLocationsProjectId(scope, user, clientId) {
  const fromScope = Number(scope?.selectedProjectId);
  if (Number.isInteger(fromScope) && fromScope > 0) return fromScope;
  const fromEnv = Number(DEFAULT_PROJECT_ID);
  if (Number.isInteger(fromEnv) && fromEnv > 0) return fromEnv;
  const projects = getSortedProjectOptions(user, clientId || scope?.selectedClientId || null);
  if (projects.length > 0) return Number(projects[0].id);
  return null;
}

function locationTypeId(type) {
  return type?.value ?? type?.id;
}

function locationTypeLevel(type) {
  const lvl = type?.level;
  const n = Number(lvl);
  return Number.isFinite(n) ? n : 0;
}

function locationTypeLabel(type) {
  return type?.label ?? type?.type_name ?? type?.name ?? '';
}

function resolveDefaultFormClientId(scopeClientId, clientOptions, hasClients) {
  if (!hasClients || clientOptions.length === 0) return '';
  if (
    scopeClientId &&
    clientOptions.some((c) => String(c.id) === String(scopeClientId))
  ) {
    return String(scopeClientId);
  }
  return String(clientOptions[0].id);
}

function resolveDefaultFormProjectId(scopeProjectId, projectOptions) {
  if (projectOptions.length === 0) return '';
  if (
    scopeProjectId &&
    projectOptions.some((p) => String(p.id) === String(scopeProjectId))
  ) {
    return String(scopeProjectId);
  }
  return String(projectOptions[0].id);
}

function findAreaTypeId(locationTypes) {
  const match = (locationTypes || []).find((t) => isAreaParentType(t));
  return match ? Number(locationTypeId(match)) : null;
}

function findStateTypeId(locationTypes) {
  const match = (locationTypes || []).find((t) => {
    const code = String(t.type_code ?? t.code ?? '').toUpperCase();
    return code.includes('STATE') || locationTypeLevel(t) === 1;
  });
  return match ? Number(locationTypeId(match)) : null;
}

/** Parent type for create form — QTR→Area, Area→Depot, Depot→State, then level fallback. */
function getParentLocationTypeId(locationTypes, selectedTypeId) {
  const typeId = Number(selectedTypeId);
  if (!typeId || !locationTypes.length) return null;

  const selected = locationTypes.find((t) => Number(locationTypeId(t)) === typeId);
  if (!selected) return null;

  const selectedCode = String(selected.type_code ?? selected.code ?? '').toUpperCase();
  const selectedName = locationTypeLabel(selected).toLowerCase();

  let parentMatcher = null;
  if (selectedCode.includes('QTR') || /\bqtr\b/.test(selectedName)) {
    parentMatcher = (t) => isAreaParentType(t);
  } else if (isAreaParentType(selected)) {
    parentMatcher = (t) => isDepotParentType(t);
  } else if (isDepotParentType(selected)) {
    parentMatcher = (t) => {
      const code = String(t.type_code ?? t.code ?? '').toUpperCase();
      return code.includes('STATE') || locationTypeLevel(t) === 1;
    };
  }

  if (parentMatcher) {
    const semantic = locationTypes.find(parentMatcher);
    if (semantic) return Number(locationTypeId(semantic));
  }

  const selectedLevel = locationTypeLevel(selected);
  const parentType = [...locationTypes]
    .filter((t) => locationTypeLevel(t) < selectedLevel)
    .sort((a, b) => locationTypeLevel(b) - locationTypeLevel(a))[0];

  return parentType ? Number(locationTypeId(parentType)) : null;
}

/** State rows often use `CODE-Name` (e.g. TS-Telangana). */
function formatStateLocationDisplay(loc) {
  if (!loc) return '';
  const name = String(loc.name ?? '').trim();
  const code = String(loc.code ?? '').trim();
  if (code && code !== '—' && name && name !== '—') return `${code}-${name}`;
  return name || code;
}

function locationDisplayName(raw) {
  const name = raw?.name;
  if (name != null && name !== '' && name !== '—') return String(name).trim();
  const label = String(raw?.label ?? '').trim();
  if (!label) return '';
  const dash = label.lastIndexOf('-');
  if (dash > 0 && dash < label.length - 1) return label.slice(dash + 1).trim();
  return label;
}

function isAreaParentType(type) {
  if (!type) return false;
  const code = String(type.type_code ?? type.code ?? '').toUpperCase();
  const name = locationTypeLabel(type).toLowerCase();
  return code.includes('AREA') || /\barea\b/.test(name);
}

function isDepotParentType(type) {
  if (!type) return false;
  const code = String(type.type_code ?? type.code ?? '').toUpperCase();
  const name = locationTypeLabel(type).toLowerCase();
  return (
    code.includes('DEPOT') ||
    code.includes('DEPT') ||
    /\bdepot\b/.test(name) ||
    /\bdept\b/.test(name)
  );
}

function parseHierarchyFields(raw) {
  const h = raw?.hierarchy;
  if (Array.isArray(h)) {
    const byLevel = (level) => {
      const item = h.find(
        (x) => Number(x.level ?? x.location_type_level ?? x.type_level) === level,
      );
      return item?.name ?? item?.location_name ?? item?.label ?? null;
    };
    return {
      state: byLevel(1),
      depot: byLevel(2),
      area: byLevel(3),
      qtr: byLevel(4),
    };
  }
  if (h && typeof h === 'object') {
    return {
      state: h.state ?? h.State ?? null,
      depot: h.depot ?? h.Depot ?? h.dept ?? h.Dept ?? null,
      area: h.area ?? h.Area ?? null,
      qtr: h.qtr ?? h.Qtr ?? null,
    };
  }
  return { state: null, depot: null, area: null, qtr: null };
}

function isAreaParentPickerLabel(parentTypeLabel) {
  return /\barea\b/i.test(String(parentTypeLabel ?? '').trim());
}

function isDepotParentPickerLabel(parentTypeLabel) {
  const label = String(parentTypeLabel ?? '').toLowerCase();
  return /\bdepot\b/.test(label) || /\bdept\b/.test(label) || /\bdepartment\b/.test(label);
}

function findDepotTypeId(locationTypes) {
  const match = (locationTypes || []).find((t) => {
    if (locationTypeLevel(t) === 2) return true;
    return isDepotParentType(t);
  });
  return match ? Number(locationTypeId(match)) : null;
}

function dedupeNormalizedLocations(locations) {
  const map = new Map();
  for (const loc of locations || []) {
    const row = loc?.id != null ? loc : normalizeLocation(loc);
    if (row?.id == null) continue;
    map.set(Number(row.id), row);
  }
  return [...map.values()];
}

function isAreaLocationRow(row, typeMap) {
  const typeId = Number(row?.location_type_id);
  const meta = typeMap?.get(typeId);
  if (meta?.code?.includes('AREA')) return true;
  if (meta?.level === 3) return true;
  return isAreaParentType({
    type_code: row?.location_type_code,
    code: row?.code,
    level: row?.location_type_level,
    type_name: row?.location_type_name,
  });
}

function rowMatchesParentLocationType(row, parentTypeId, parentTypeLabel, locationTypes) {
  const rowTypeId = Number(row?.location_type_id);
  const parentIdNum = Number(parentTypeId);
  if (rowTypeId === parentIdNum) return true;

  if (isAreaParentPickerLabel(parentTypeLabel) && isAreaLocationRow(row, buildLocationTypeMetaMap(locationTypes))) {
    return true;
  }
  if (isDepotParentPickerLabel(parentTypeLabel)) {
    return isDepotLocation(row, buildLocationTypeMetaMap(locationTypes));
  }

  const parentType = (locationTypes || []).find(
    (t) => Number(locationTypeId(t)) === parentIdNum,
  );
  if (!parentType || !rowTypeId) return false;
  const parentLevel = locationTypeLevel(parentType);
  const rowLevel = locationTypeLevel({
    level: row.location_type_level,
    type_code: row.location_type_code,
  });
  return rowLevel > 0 && parentLevel > 0 && rowLevel === parentLevel;
}

function readScalarOrObjectName(value) {
  if (value == null || value === '' || value === '—') return '';
  if (typeof value === 'object') {
    return preferNonEmptyField(value.name, value.label, value.type_name);
  }
  const text = String(value).trim();
  if (!text || /^\d+$/.test(text)) return '';
  return text;
}

function lookupDepotNodeName(depotLocationId, byId) {
  if (depotLocationId == null || depotLocationId === '') return '';
  const node = byId.get(Number(depotLocationId));
  return node ? locationDisplayName(node) || String(node.name ?? '').trim() : '';
}

function resolveDepotLabelForAreaRow(row, byId, typeMap) {
  const hierarchyFields = parseHierarchyFields(row);

  const fromDepotFk = lookupDepotNodeName(row.depot_location_id, byId);
  if (fromDepotFk) return fromDepotFk;

  const fromDepotField = readScalarOrObjectName(row.depot);
  if (fromDepotField) return fromDepotField;

  const fromNames = preferNonEmptyField(
    row.depot_name,
    row.department,
    hierarchyFields.depot,
    deptNameFromHierarchyPath(row.hierarchyPath),
  );
  if (fromNames) return fromNames;

  const parentId = row.parent_id;
  if (parentId != null) {
    const parentNode = byId.get(Number(parentId));
    if (parentNode && isDepotLocation(parentNode, typeMap)) {
      return locationDisplayName(parentNode) || String(parentNode.name ?? '').trim();
    }
  }

  if (row.parent_name && row.parent_name !== '—') {
    const parentNode = byId.get(Number(parentId));
    if (!parentNode || isDepotLocation(parentNode, typeMap)) {
      return String(row.parent_name).trim();
    }
  }

  const nested = row.parent ?? row.parentLocation ?? row.parent_location;
  if (nested) {
    const nestedName = readScalarOrObjectName(nested) || locationDisplayName(nested);
    if (nestedName) return nestedName;
  }

  return walkToDepotParent(row, byId, typeMap);
}

/** Depot name for each area row — built from the full project index. */
function buildAreaDepotNameMap(indexRows, locationTypes) {
  const typeList = locationTypes || [];
  const typeMap = buildLocationTypeMetaMap(typeList);
  const normalized = (indexRows || []).map(normalizeLocation).filter((r) => r.id != null);
  if (!normalized.length) return new Map();

  const enriched = enrichLocationsWithHierarchy(
    normalized,
    { locationTypes: typeList },
    normalized,
  );
  const byId = new Map();
  for (const row of [...normalized, ...enriched]) {
    byId.set(Number(row.id), row);
  }

  const parentTypeForArea = typeList.find((t) => isAreaParentType(t)) ?? null;
  const map = new Map();
  for (const row of enriched) {
    if (!isAreaLocationRow(row, typeMap)) continue;
    const dept = preferNonEmptyField(
      row.depot !== '—' ? row.depot : '',
      resolveDepotLabelForAreaRow(row, byId, typeMap),
      resolveDeptNameFromRow(row, parentTypeForArea, byId, typeMap),
    );
    if (dept && dept !== '—') map.set(Number(row.id), dept);
  }

  for (const row of normalized) {
    if (!isAreaLocationRow(row, typeMap)) continue;
    const id = Number(row.id);
    if (map.has(id)) continue;
    const dept = resolveDepotLabelForAreaRow(row, byId, typeMap);
    if (dept && dept !== '—') map.set(id, dept);
  }

  return map;
}

function resolveParentSuffixKind(parentType, parentTypeLabel) {
  const label = String(parentTypeLabel || locationTypeLabel(parentType) || '').toLowerCase();
  if (/\barea\b/.test(label)) return 'dept';
  if (/\bdepot\b/.test(label) || /\bdept\b/.test(label) || /\bdepartment\b/.test(label)) {
    return 'state';
  }
  const level = locationTypeLevel(parentType);
  if (level === 3 || level === 4) return 'dept';
  if (level === 2) return 'state';
  if (isAreaParentType(parentType)) return 'dept';
  if (isDepotParentType(parentType)) return 'state';
  return null;
}

function preferNonEmptyField(...values) {
  for (const v of values) {
    if (v != null && v !== '' && v !== '—') return String(v).trim();
  }
  return '';
}

function isDepotLocation(row, typeMap) {
  const typeId = Number(row?.location_type_id);
  const meta = typeMap?.get(typeId);
  if (meta?.code?.includes('DEPOT') || meta?.code?.includes('DEPT')) return true;
  if (meta?.level === 2) return true;
  return isDepotParentType({
    type_code: row?.location_type_code,
    code: row?.code,
    level: row?.location_type_level,
    type_name: row?.location_type_name,
  });
}

function walkToDepotParent(row, byId, typeMap) {
  let current = row;
  const seen = new Set();
  for (let depth = 0; depth < 8 && current; depth += 1) {
    const currentId = Number(current.id);
    if (!Number.isFinite(currentId) || seen.has(currentId)) break;
    seen.add(currentId);
    const parentId = current.parent_id ?? current.parentId;
    if (parentId == null || parentId === '') break;
    const parent = byId.get(Number(parentId));
    if (!parent) break;
    if (isDepotLocation(parent, typeMap)) {
      return locationDisplayName(parent) || String(parent.name ?? '').trim();
    }
    current = parent;
  }
  return '';
}

function deptNameFromHierarchyPath(path) {
  if (!path || path === '—') return '';
  const parts = String(path)
    .split(/\s*[›>]\s*/)
    .map((p) => p.trim())
    .filter((p) => p && p !== '—');
  if (parts.length >= 2) return parts[1];
  return '';
}

function resolveDeptNameFromRow(row, parentType, byId, typeMap) {
  const fromEnrichedDepot = preferNonEmptyField(row.depot, row.depot_name);
  if (fromEnrichedDepot) return fromEnrichedDepot;

  const fromParentName = preferNonEmptyField(row.parent_name);
  if (fromParentName) return fromParentName;

  const fromPath = deptNameFromHierarchyPath(row.hierarchyPath);
  if (fromPath) return fromPath;

  const fromWalk = walkToDepotParent(row, byId, typeMap);
  if (fromWalk) return fromWalk;

  const parentNode = resolveParentNode(row, byId);
  if (parentNode) {
    const direct = locationDisplayName(parentNode) || String(parentNode.name ?? '').trim();
    if (direct) return direct;
  }

  const nestedParent = row.parent ?? row.parentLocation ?? row.parent_location;
  if (nestedParent) {
    const nestedName = locationDisplayName(nestedParent) || String(nestedParent.name ?? '').trim();
    if (nestedName) return nestedName;
  }

  const h = row.hierarchy;
  if (Array.isArray(h) && h.length > 0) {
    const parentLevel = locationTypeLevel(parentType);
    const depotLevel = parentLevel > 1 ? parentLevel - 1 : 2;
    const byParentLevel = h.find(
      (x) => Number(x.level ?? x.location_type_level ?? x.type_level) === depotLevel,
    );
    if (byParentLevel?.name) return String(byParentLevel.name).trim();

    const depotEntry = h.find((x) => {
      const code = String(
        x.type_code ?? x.code ?? x.location_type_code ?? '',
      ).toUpperCase();
      const typeName = String(x.type_name ?? x.location_type_name ?? '').toLowerCase();
      return (
        code.includes('DEPOT') ||
        code.includes('DEPT') ||
        /\bdepot\b/.test(typeName) ||
        /\bdept\b/.test(typeName)
      );
    });
    if (depotEntry?.name) return String(depotEntry.name).trim();

    const sorted = [...h].sort(
      (a, b) =>
        Number(a.level ?? a.location_type_level ?? 0) -
        Number(b.level ?? b.location_type_level ?? 0),
    );
    const areaIdx = sorted.findIndex((x) => {
      const code = String(x.type_code ?? x.code ?? '').toUpperCase();
      return code.includes('AREA') || Number(x.level) === 3;
    });
    if (areaIdx > 0 && sorted[areaIdx - 1]?.name) {
      return String(sorted[areaIdx - 1].name).trim();
    }
    if (sorted.length >= 2 && sorted[sorted.length - 2]?.name) {
      return String(sorted[sorted.length - 2].name).trim();
    }
  }

  const hierarchy = parseHierarchyFields(row);
  if (hierarchy.depot) return String(hierarchy.depot).trim();
  if (row.parent_name && row.parent_name !== '—') return String(row.parent_name).trim();
  if (row.depot_name && row.depot_name !== '—') return String(row.depot_name).trim();
  if (row.depot && row.depot !== '—') return String(row.depot).trim();
  return '';
}

function resolveParentNode(row, byId) {
  if (!byId?.size) return null;
  const parentId = row.parent_id ?? row.parentId;
  if (parentId == null || parentId === '') return null;
  return byId.get(Number(parentId)) ?? null;
}

function findStateNodeByName(name, byId) {
  const target = String(name ?? '').trim().toLowerCase();
  if (!target || !byId?.size) return null;
  for (const node of byId.values()) {
    const nodeName = String(node.name ?? '').trim().toLowerCase();
    if (nodeName !== target) continue;
    const code = String(node.code ?? '').toUpperCase();
    const typeCode = String(node.location_type_code ?? '').toUpperCase();
    if (code.includes('STATE') || typeCode.includes('STATE') || locationTypeLevel(node) === 1) {
      return node;
    }
  }
  return null;
}

function mergeRowWithIndex(row, byId) {
  const indexed = byId.get(Number(row.id));
  if (!indexed) return row;
  return {
    ...indexed,
    ...row,
    id: row.id ?? indexed.id,
    parent_id: row.parent_id ?? indexed.parent_id ?? null,
    depot_location_id: row.depot_location_id ?? indexed.depot_location_id ?? null,
    name: preferNonEmptyField(row.name, indexed.name) || row.name || indexed.name,
    code: preferNonEmptyField(row.code, indexed.code) || row.code || indexed.code,
    depot: preferNonEmptyField(row.depot, indexed.depot) || null,
    state: preferNonEmptyField(row.state, indexed.state) || null,
    parent_name: preferNonEmptyField(row.parent_name, indexed.parent_name) || null,
    hierarchyPath: preferNonEmptyField(row.hierarchyPath, indexed.hierarchyPath) || null,
    hierarchy: row.hierarchy ?? indexed.hierarchy ?? null,
  };
}

function resolveParentChoiceRow(raw, byId) {
  const fromDropdown = normalizeLocation(raw);
  const fromIndex = byId.get(Number(fromDropdown.id));
  if (!fromIndex) return fromDropdown;
  return normalizeLocation({
    ...fromDropdown,
    ...fromIndex,
    id: fromDropdown.id,
    parent_id: fromIndex.parent_id ?? fromDropdown.parent_id,
    depot_location_id: fromIndex.depot_location_id ?? fromDropdown.depot_location_id ?? null,
    hierarchy: fromIndex.hierarchy ?? fromDropdown.hierarchy,
    hierarchyPath: preferNonEmptyField(fromIndex.hierarchyPath, fromDropdown.hierarchyPath) || null,
    depot: preferNonEmptyField(fromIndex.depot, fromDropdown.depot) || null,
    depot_name: preferNonEmptyField(fromIndex.depot_name, fromDropdown.depot_name) || null,
    parent_name: preferNonEmptyField(fromIndex.parent_name, fromDropdown.parent_name) || null,
    state: preferNonEmptyField(fromIndex.state, fromDropdown.state) || null,
    name: preferNonEmptyField(fromDropdown.name, fromIndex.name) || fromDropdown.name,
  });
}

function resolveParentSuffixText(row, suffixKind, byId) {
  const hierarchy = parseHierarchyFields(row);
  const parentNode = resolveParentNode(row, byId);
  const nestedParent = row.parent ?? row.parentLocation ?? row.parent_location;

  if (suffixKind === 'dept') {
    let dept = '';
    if (parentNode) {
      dept = locationDisplayName(parentNode) || String(parentNode.name ?? '').trim();
    }
    if (!dept && nestedParent) {
      dept = locationDisplayName(nestedParent) || String(nestedParent.name ?? '').trim();
    }
    if (!dept && row.parent_name && row.parent_name !== '—') {
      dept = String(row.parent_name).trim();
    }
    if (!dept && row.depot_name && row.depot_name !== '—') {
      dept = String(row.depot_name).trim();
    }
    if (!dept && row.depot && row.depot !== '—') {
      dept = String(row.depot).trim();
    }
    if (!dept && hierarchy.depot) {
      dept = String(hierarchy.depot).trim();
    }
    if (dept) return ` > (Dept =${dept})`;
  }

  if (suffixKind === 'state') {
    let stateLabel = '';
    if (parentNode) stateLabel = formatStateLocationDisplay(parentNode);
    if (!stateLabel && nestedParent) stateLabel = formatStateLocationDisplay(nestedParent);
    if (!stateLabel && row.state_name && row.state_name !== '—') {
      stateLabel = String(row.state_name).trim();
    }
    if (!stateLabel && row.state && row.state !== '—') {
      const stateNode = findStateNodeByName(row.state, byId);
      stateLabel = stateNode
        ? formatStateLocationDisplay(stateNode)
        : String(row.state).trim();
    }
    if (!stateLabel && hierarchy.state) {
      const stateNode = findStateNodeByName(hierarchy.state, byId);
      stateLabel = stateNode
        ? formatStateLocationDisplay(stateNode)
        : String(hierarchy.state).trim();
    }
    if (stateLabel) return ` > (State =${stateLabel})`;
  }

  return '';
}

function buildParentSelectOptionParts(row, parentType, parentTypeLabel, byId, typeMap, deptOverride) {
  const id = row.id ?? row.value;
  const name = locationDisplayName(row) || `Location #${id}`;
  const primary = `${name} #${id}`;
  const suffixKind = resolveParentSuffixKind(parentType, parentTypeLabel);
  const areaPicker = isAreaParentPickerLabel(parentTypeLabel);

  if (suffixKind === 'dept' || areaPicker) {
    const dept =
      deptOverride ||
      preferNonEmptyField(
        row.depot,
        row.depot_name,
        deptNameFromHierarchyPath(row.hierarchyPath),
        row.parent_name,
      ) ||
      resolveDeptNameFromRow(row, parentType, byId, typeMap);
    if (dept) return { primary, suffix: ` > (Dept =${dept})` };
  }

  if (suffixKind === 'state') {
    const parentNode = resolveParentNode(row, byId);
    let stateLabel = parentNode ? formatStateLocationDisplay(parentNode) : '';
    if (!stateLabel && row.parent_name && row.parent_name !== '—') {
      stateLabel = String(row.parent_name).trim();
    }
    if (!stateLabel) {
      stateLabel = extractSuffixValue(resolveParentSuffixText(row, 'state', byId));
    }
    if (!stateLabel && row.state && row.state !== '—') {
      const stateNode = findStateNodeByName(row.state, byId);
      stateLabel = stateNode
        ? formatStateLocationDisplay(stateNode)
        : String(row.state).trim();
    }
    if (stateLabel) return { primary, suffix: ` > (State =${stateLabel})` };
  }

  const fallbackDept = resolveParentSuffixText(row, 'dept', byId);
  if (fallbackDept) return { primary, suffix: fallbackDept };

  const fallbackState = resolveParentSuffixText(row, 'state', byId);
  if (fallbackState) return { primary, suffix: fallbackState };

  return { primary, suffix: '' };
}

function extractSuffixValue(suffixText) {
  const match = String(suffixText ?? '').match(/>\s*\((?:Dept|State)\s*=(.+)\)\s*$/i);
  return match ? match[1].trim() : '';
}

function formatParentSelectOptionSearchText(row) {
  const id = row.id ?? row.value;
  const name = locationDisplayName(row);
  const code =
    row.code != null && row.code !== '' && row.code !== '—'
      ? String(row.code).trim()
      : '';
  const label = String(row.label ?? '').trim();
  const tokens = new Set();
  if (name) tokens.add(name);
  if (code) tokens.add(code);
  if (label) tokens.add(label);
  if (id != null && id !== '') tokens.add(String(id));
  return [...tokens].join(' ');
}

function normalizeLocation(raw) {
  const projectId = raw.project_id ?? raw.projectId ?? raw.project?.id;
  const nestedParent = raw.parent ?? raw.parentLocation ?? raw.parent_location;
  const depotLocationId =
    raw.depot_location_id ??
    raw.depotLocationId ??
    raw.depot_id ??
    raw.depotId ??
    null;
  let parentId =
    raw.parent_id ??
    raw.parentId ??
    raw.parent_location_id ??
    raw.parentLocationId ??
    nestedParent?.id ??
    nestedParent?.value ??
    null;
  if ((parentId == null || parentId === '') && depotLocationId != null && depotLocationId !== '') {
    parentId = depotLocationId;
  }
  const lat = raw.latitude ?? raw.lat;
  const lng = raw.longitude ?? raw.lng ?? raw.lon;
  const rawId = raw.id ?? raw.value;
  const displayName = locationDisplayName(raw);
  const hierarchyFields = parseHierarchyFields(raw);
  const depotLabel = preferNonEmptyField(
    readScalarOrObjectName(raw.depot),
    readScalarOrObjectName(raw.dept),
    readScalarOrObjectName(raw.department),
    raw.depot_name,
    raw.depotName,
    raw.depot?.name,
    raw.department?.name,
    hierarchyFields.depot,
  );

  return {
    id: rawId != null && rawId !== '' ? Number(rawId) : undefined,
    project_id: projectId != null ? Number(projectId) : '',
    location_type_id:
      raw.location_type_id ?? raw.locationTypeId ?? raw.location_type?.id ?? raw.locationType?.id ?? '',
    parent_id: parentId != null && parentId !== '' ? Number(parentId) : null,
    depot_location_id:
      depotLocationId != null && depotLocationId !== '' ? Number(depotLocationId) : null,
    parent_name:
      preferNonEmptyField(
        raw.parent_name,
        raw.parentName,
        raw.parent_location_name,
        nestedParent?.name,
        readScalarOrObjectName(nestedParent),
      ) || null,
    depot_name:
      preferNonEmptyField(
        raw.depot_name,
        raw.depotName,
        raw.depot?.name,
        raw.department?.name,
        depotLabel,
      ) || null,
    state_name: raw.state_name ?? raw.stateName ?? null,
    parent: nestedParent ?? raw.parent ?? null,
    name: displayName || raw.name || '—',
    code: raw.code ?? '—',
    latitude: lat,
    longitude: lng,
    address: raw.address ?? '—',
    pincode: raw.pincode ?? '—',
    location_type_name:
      raw.location_type?.type_name ||
      raw.location_type?.name ||
      raw.locationType?.type_name ||
      raw.locationType?.name ||
      '',
    location_type_code:
      raw.location_type?.type_code ||
      raw.locationType?.type_code ||
      raw.location_type_code ||
      '',
    location_type_level:
      raw.location_type?.level ??
      raw.locationType?.level ??
      raw.location_type_level ??
      null,
    hierarchy: raw.hierarchy ?? raw.hierarchy_path ?? raw.hierarchyPath ?? null,
    hierarchyPath:
      raw.hierarchyPath ??
      raw.hierarchy_path ??
      (typeof raw.hierarchy === 'string' ? raw.hierarchy : null) ??
      null,
    state: readScalarOrObjectName(raw.state) || hierarchyFields.state || null,
    depot: depotLabel || hierarchyFields.depot || null,
    area: raw.area ?? hierarchyFields.area ?? null,
    qtr: raw.qtr ?? hierarchyFields.qtr ?? null,
  };
}

export default function Locations() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [hierarchyIndexRows, setHierarchyIndexRows] = useState([]);
  const [hierarchyLocationTypes, setHierarchyLocationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [localSearch, setLocalSearch] = useState('');
  const [filterByField, setFilterByField] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [pageFlash] = useState('');
  const [formClientId, setFormClientId] = useState('');
  const [bulkClientId, setBulkClientId] = useState('');
  const [bulkProjectId, setBulkProjectId] = useState('');
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkSuccess, setBulkSuccess] = useState('');
  const bulkFileInputRef = useRef(null);
  const pageFlashTimeoutRef = useRef(null);
  const projectFormIndexRequestRef = useRef(0);

  const [locationTypes, setLocationTypes] = useState([]);
  const [scopeLocationTypes, setScopeLocationTypes] = useState([]);
  const [projectFormIndex, setProjectFormIndex] = useState([]);
  const [projectFormIndexLoading, setProjectFormIndexLoading] = useState(false);

  const [form, setForm] = useState({
    project_id: '',
    location_type_id: '',
    parent_id: '',
    name: '',
    code: '',
    latitude: '',
    longitude: '',
    address: '',
    pincode: '',
  });

  const scope = useClientProjectScope();
  const hasClients = clientListExists(user);
  const clientOptions = useMemo(() => getSortedClientOptions(user), [user]);
  const projectOptions = useMemo(
    () => getSortedProjectOptions(user, hasClients ? formClientId || null : null),
    [user, formClientId, hasClients],
  );

  const bulkProjectOptions = useMemo(
    () => getSortedProjectOptions(user, hasClients ? bulkClientId || null : null),
    [user, bulkClientId, hasClients],
  );

  useEffect(() => {
    if (!hasClients || clientOptions.length === 0) return;
    setBulkClientId((prev) => (prev === '' ? String(clientOptions[0].id) : prev));
  }, [hasClients, clientOptions]);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      const projectId = resolveLocationsProjectId(scope, user, scope.selectedClientId);
      if (!silent) setLoading(true);
      setError('');
      try {
        const typesPromise = projectId
          ? fetchLocationTypes(projectId).catch(() => [])
          : Promise.resolve([]);

        if (projectId) {
          const [{ active, all }, types] = await Promise.all([
            fetchAllLocationsForHierarchy(projectId),
            typesPromise,
          ]);
          const normalizedActive = active.map(normalizeLocation);
          const normalizedAll = all.map(normalizeLocation);
          setRows(normalizedActive);
          setHierarchyIndexRows(normalizedAll);
          if (types.length > 0) {
            setHierarchyLocationTypes(types);
            setScopeLocationTypes(types);
          }
        } else {
          const [active, types] = await Promise.all([
            fetchAllLocations({ isActive: 'true' }),
            typesPromise,
          ]);
          const normalizedActive = active.map(normalizeLocation);
          setRows(normalizedActive);
          setHierarchyIndexRows(normalizedActive);
          if (types.length > 0) {
            setHierarchyLocationTypes(types);
            setScopeLocationTypes(types);
          }
          if (!silent) setLoading(false);
          fetchAllLocations({ isActive: 'all' })
            .then((all) => setHierarchyIndexRows(all.map(normalizeLocation)))
            .catch(() => {});
          return;
        }
      } catch (err) {
        setError(humanizeError(err, 'Could not load locations. Please try again.'));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [scope.selectedProjectId, scope.selectedClientId, user],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(
    () => () => {
      if (pageFlashTimeoutRef.current) {
        clearTimeout(pageFlashTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const projectId = Number(form.project_id);
    if (!Number.isInteger(projectId) || projectId <= 0) {
      setLocationTypes([]);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const types = await fetchLocationTypes(projectId);
        if (!cancelled) setLocationTypes(types);
      } catch {
        if (!cancelled) setLocationTypes([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form.project_id]);

  const parentLocationTypeId = useMemo(
    () => getParentLocationTypeId(locationTypes, form.location_type_id),
    [locationTypes, form.location_type_id],
  );

  const activeFormProjectId = Number(form.project_id) || Number(scope.selectedProjectId);

  const loadProjectFormIndex = useCallback(
    async (
      projectId,
      { types: typesOverride, requestId, hasSeed = false, skipFullLocationFetch = false } = {},
    ) => {
      const pid = Number(projectId);
      if (!Number.isInteger(pid) || pid <= 0) {
        setProjectFormIndex([]);
        setProjectFormIndexLoading(false);
        return;
      }

      const isStale = () => requestId != null && requestId !== projectFormIndexRequestRef.current;

      if (!hasSeed) {
        setProjectFormIndexLoading(true);
      }

      try {
        const types =
          typesOverride?.length > 0
            ? typesOverride
            : await fetchLocationTypes(pid);
        if (isStale()) return;

        const areaTypeId = findAreaTypeId(types);
        const depotTypeId = findDepotTypeId(types);

        const quickFetches = [];
        if (areaTypeId != null) {
          quickFetches.push(fetchLocationsDropdown(pid, areaTypeId).catch(() => []));
        }
        if (depotTypeId != null) {
          quickFetches.push(fetchLocationsDropdown(pid, depotTypeId).catch(() => []));
        }

        if (quickFetches.length > 0) {
          const quickResults = await Promise.all(quickFetches);
          if (isStale()) return;
          const quickMerged = dedupeNormalizedLocations(
            quickResults.flat().map(normalizeLocation),
          ).filter((r) => r.id != null);
          setProjectFormIndex((prev) => dedupeNormalizedLocations([...prev, ...quickMerged]));
        }

        setProjectFormIndexLoading(false);

        if (!skipFullLocationFetch) {
          const allLocs = await fetchAllLocations({
            projectId: pid,
            project_id: pid,
            isActive: 'all',
          });
          if (isStale()) return;
          setProjectFormIndex((prev) =>
            dedupeNormalizedLocations([
              ...prev,
              ...allLocs.map(normalizeLocation).filter((r) => r.id != null),
            ]),
          );
        }
      } catch {
        if (!isStale()) {
          if (!hasSeed) setProjectFormIndex([]);
          setProjectFormIndexLoading(false);
        }
      }
    },
    [],
  );

  const seedProjectFormIndex = useCallback(
    (projectId) => {
      const pid = Number(projectId);
      if (!Number.isInteger(pid) || pid <= 0) return [];
      const forProject = (r) =>
        r?.id != null && (!r.project_id || Number(r.project_id) === pid);
      return dedupeNormalizedLocations([
        ...hierarchyIndexRows.map(normalizeLocation).filter(forProject),
        ...rows.map(normalizeLocation).filter(forProject),
      ]);
    },
    [hierarchyIndexRows, rows],
  );

  useEffect(() => {
    if (!showCreateModal) return undefined;
    if (!Number.isInteger(activeFormProjectId) || activeFormProjectId <= 0) {
      setProjectFormIndex([]);
      setProjectFormIndexLoading(false);
      return undefined;
    }

    const requestId = projectFormIndexRequestRef.current + 1;
    projectFormIndexRequestRef.current = requestId;

    const seed = seedProjectFormIndex(activeFormProjectId);
    const indexCount = hierarchyIndexRows.filter(
      (r) =>
        r?.id != null &&
        (!r.project_id || Number(r.project_id) === activeFormProjectId),
    ).length;

    if (seed.length > 0) {
      setProjectFormIndex(seed);
      setProjectFormIndexLoading(false);
    } else {
      setProjectFormIndex([]);
      setProjectFormIndexLoading(true);
    }

    const typesForProject =
      Number(form.project_id) === activeFormProjectId && locationTypes.length > 0
        ? locationTypes
        : null;

    loadProjectFormIndex(activeFormProjectId, {
      types: typesForProject,
      requestId,
      hasSeed: seed.length > 0,
      skipFullLocationFetch: indexCount >= 15,
    });

    return undefined;
  }, [
    showCreateModal,
    activeFormProjectId,
    loadProjectFormIndex,
    seedProjectFormIndex,
    form.project_id,
    locationTypes,
    hierarchyIndexRows,
  ]);

  useEffect(() => {
    if (!showCreateModal || !Number.isInteger(activeFormProjectId) || activeFormProjectId <= 0) {
      return;
    }
    const seed = seedProjectFormIndex(activeFormProjectId);
    if (seed.length === 0) return;
    setProjectFormIndex((prev) => dedupeNormalizedLocations([...prev, ...seed]));
    setProjectFormIndexLoading(false);
  }, [showCreateModal, activeFormProjectId, seedProjectFormIndex]);
  const projectLabelMap = useMemo(() => getProjectLabelMap(user), [user]);

  const locationTypesForEnrichment = useMemo(() => {
    const merged = [...hierarchyLocationTypes];
    for (const t of scopeLocationTypes) {
      const id = Number(locationTypeId(t));
      if (!merged.some((m) => Number(locationTypeId(m)) === id)) merged.push(t);
    }
    return merged;
  }, [hierarchyLocationTypes, scopeLocationTypes]);

  const enrichedRows = useMemo(
    () =>
      enrichLocationsWithHierarchy(
        rows,
        { locationTypes: locationTypesForEnrichment },
        hierarchyIndexRows,
      ),
    [rows, hierarchyIndexRows, locationTypesForEnrichment],
  );

  const activeFilterMeta = useMemo(() => getActiveFilterMeta(filterByField), [filterByField]);
  const filterByDropdownOptions = useMemo(() => getFilterByDropdownOptions(), []);
  const locationDropdownOptions = useMemo(
    () => [
      { value: '', label: activeFilterMeta.allLabel },
      ...getLocationOptionsFromRows(enrichedRows, activeFilterMeta.field).map((d) => ({
        value: d,
        label: d,
      })),
    ],
    [enrichedRows, activeFilterMeta],
  );

  const filteredFlatRows = useMemo(() => {
    let data = enrichedRows;
    if (scope.selectedProjectId) {
      data = data.filter((r) => String(r.project_id) === String(scope.selectedProjectId));
    } else if (scope.selectedClientId) {
      const projectIds = new Set(scope.projectOptions.map((p) => String(p.id)));
      data = data.filter((r) => projectIds.has(String(r.project_id)));
    }
    const q = localSearch.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => {
      const path = [
        projectLabelMap[String(row.project_id)] || '',
        row.state,
        row.depot,
        row.area,
        row.qtr,
        row.hierarchyPath,
        row.name,
        row.code,
        row.address,
        row.location_type_name,
        row.typeLabel,
      ]
        .join(' ')
        .toLowerCase();
      return path.includes(q);
    });
  }, [
    enrichedRows,
    scope.selectedClientId,
    scope.selectedProjectId,
    scope.projectOptions,
    localSearch,
    projectLabelMap,
  ]);

  const displayRows = useMemo(() => {
    const filtered = applyHierarchyFilters(filteredFlatRows, filterByField, locationFilter);
    return sortLocationRows(filtered);
  }, [filteredFlatRows, filterByField, locationFilter]);

  useEffect(() => {
    setPage(1);
  }, [localSearch, filterByField, locationFilter, scope.selectedClientId, scope.selectedProjectId]);

  const totalPages = Math.max(1, Math.ceil(displayRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const slOffset = (safePage - 1) * PAGE_SIZE;
  const pageRows = useMemo(
    () => displayRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [displayRows, safePage],
  );
  const pageList = useMemo(() => buildPageList(safePage, totalPages), [safePage, totalPages]);

  const hierarchyColumns = useMemo(
    () => getHierarchyColumnDefs(locationTypesForEnrichment),
    [locationTypesForEnrichment],
  );

  const locColumns = useMemo(() => {
    const cols = [
      {
        id: 'sl',
        header: 'Sl. No',
        cell: (_, idx) => String(slOffset + idx + 1),
      },
      ...hierarchyColumns.map((col) => ({
        id: col.field,
        header: col.header,
        cell: (r) => <span className="locLevelName">{r[col.field] || '—'}</span>,
      })),
      {
        id: 'action',
        header: 'Action',
        cell: (r) => (
          <button
            type="button"
            className="foChipBtn"
            onClick={(e) => {
              e.stopPropagation();
              const rowProjectId =
                r.project_id != null && r.project_id !== '' ? String(r.project_id) : '';

              let nextClientId = formClientId;
              if (hasClients) {
                const matchedProject = (user?.projects || []).find(
                  (p) => getProjectId(p) === rowProjectId,
                );
                // Auth profile nests client as project.client.id (not client_id).
                nextClientId = getProjectClientId(matchedProject);

                if (!nextClientId && rowProjectId) {
                  // Fall back: find any client whose project list contains this project.
                  for (const c of clientOptions) {
                    const underClient = getSortedProjectOptions(user, c.id);
                    if (underClient.some((p) => p.id === rowProjectId)) {
                      nextClientId = String(c.id);
                      break;
                    }
                  }
                }

                if (!nextClientId) {
                  nextClientId =
                    scope?.selectedClientId
                      ? String(scope.selectedClientId)
                      : clientOptions[0]?.id
                        ? String(clientOptions[0].id)
                        : '';
                }

                // eslint-disable-next-line no-console
                console.log('[EditLocation] open', {
                  locationId: r.id,
                  rowProjectId,
                  matchedProject,
                  resolvedClientId: nextClientId,
                  projectClientFromMatch: getProjectClientId(matchedProject),
                  userProjectSample: (user?.projects || []).slice(0, 2),
                });

                setFormClientId(nextClientId);
              }

              setEditingLocationId(Number(r.id));
              setForm({
                project_id: rowProjectId || form.project_id || '',
                location_type_id:
                  r.location_type_id != null && r.location_type_id !== ''
                    ? String(r.location_type_id)
                    : '',
                parent_id:
                  r.parent_id != null && r.parent_id !== '' ? String(r.parent_id) : '',
                name: r.name && r.name !== '—' ? String(r.name) : '',
                code: r.code && r.code !== '—' ? String(r.code) : '',
                latitude:
                  r.latitude != null && r.latitude !== '' && r.latitude !== '—'
                    ? String(r.latitude)
                    : '',
                longitude:
                  r.longitude != null && r.longitude !== '' && r.longitude !== '—'
                    ? String(r.longitude)
                    : '',
                address: r.address && r.address !== '—' ? String(r.address) : '',
                pincode: r.pincode && r.pincode !== '—' ? String(r.pincode) : '',
              });
              setSubmitError('');
              setShowCreateModal(true);
            }}
          >
            Edit
          </button>
        ),
      },
    ];
    return cols;
  }, [
    slOffset,
    hierarchyColumns,
    form.project_id,
    formClientId,
    hasClients,
    user?.projects,
    clientOptions,
    scope?.selectedClientId,
  ]);

  const exportCsv = () => {
    const headers = ['Sl. No', ...hierarchyColumns.map((c) => c.header)];
    const exportRows = sortLocationRows(
      applyHierarchyFilters(filteredFlatRows, filterByField, locationFilter),
    );
    const csvRows = exportRows.map((row, index) => [
      index + 1,
      ...hierarchyColumns.map((c) => row[c.field] || ''),
    ]);
    const csv = [headers, ...csvRows].map((line) => line.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'locations.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const applyDefaultFormProject = useCallback(
    (clientId) => {
      const projects = getSortedProjectOptions(
        user,
        hasClients ? clientId || null : null,
      );
      const projectId = resolveDefaultFormProjectId(
        scope.selectedProjectId,
        projects,
      );
      setForm((prev) => ({
        ...prev,
        project_id: projectId,
        location_type_id: '',
        parent_id: '',
      }));
    },
    [user, hasClients, scope.selectedProjectId],
  );

  const resetForm = useCallback(() => {
    const defaultClientId = resolveDefaultFormClientId(
      scope.selectedClientId,
      clientOptions,
      hasClients,
    );
    setFormClientId(defaultClientId);

    const projects = getSortedProjectOptions(
      user,
      hasClients ? defaultClientId || null : null,
    );
    const defaultProjectId = resolveDefaultFormProjectId(
      scope.selectedProjectId,
      projects,
    );

    setForm({
      project_id: defaultProjectId,
      location_type_id: '',
      parent_id: '',
      name: '',
      code: '',
      latitude: '',
      longitude: '',
      address: '',
      pincode: '',
    });
    setSubmitError('');
  }, [
    user,
    hasClients,
    clientOptions,
    scope.selectedClientId,
    scope.selectedProjectId,
  ]);

  const resetFormAfterCreate = () => {
    setForm((prev) => ({
      ...prev,
      parent_id: '',
      name: '',
      code: '',
      latitude: '',
      longitude: '',
      address: '',
      pincode: '',
    }));
    setSubmitError('');
  };

  const selectedLocationTypeLabel = useMemo(() => {
    const typeId = Number(form.location_type_id);
    if (!typeId) return '';
    const match = locationTypes.find((t) => Number(locationTypeId(t)) === typeId);
    return locationTypeLabel(match);
  }, [locationTypes, form.location_type_id]);

  const parentLocationTypeLabel = useMemo(() => {
    if (parentLocationTypeId == null) return '';
    const match = locationTypes.find(
      (t) => Number(locationTypeId(t)) === Number(parentLocationTypeId),
    );
    return locationTypeLabel(match);
  }, [locationTypes, parentLocationTypeId]);

  const parentLocationTypeMeta = useMemo(() => {
    if (parentLocationTypeId == null) return null;
    return (
      locationTypes.find(
        (t) => Number(locationTypeId(t)) === Number(parentLocationTypeId),
      ) ?? null
    );
  }, [locationTypes, parentLocationTypeId]);

  const parentIndexRows = useMemo(() => {
    const projectId = Number(form.project_id);
    if (!Number.isInteger(projectId) || projectId <= 0) return [];
    return hierarchyIndexRows
      .map(normalizeLocation)
      .filter(
        (r) =>
          r.id != null &&
          (!r.project_id || Number(r.project_id) === projectId),
      );
  }, [hierarchyIndexRows, form.project_id]);

  const projectLocationIndex = useMemo(() => {
    const projectId = Number(form.project_id);
    if (!Number.isInteger(projectId) || projectId <= 0) return [];
    const forProject = (r) =>
      r?.id != null && (!r.project_id || Number(r.project_id) === projectId);
    return dedupeNormalizedLocations([
      ...projectFormIndex,
      ...parentIndexRows.filter(forProject),
      ...enrichedRows.filter(forProject),
    ]);
  }, [projectFormIndex, parentIndexRows, enrichedRows, form.project_id]);

  const indexedParentChoices = useMemo(() => {
    if (parentLocationTypeId == null) return [];
    return projectLocationIndex.filter((r) =>
      rowMatchesParentLocationType(
        r,
        parentLocationTypeId,
        parentLocationTypeLabel,
        locationTypes,
      ),
    );
  }, [
    projectLocationIndex,
    parentLocationTypeId,
    parentLocationTypeLabel,
    locationTypes,
  ]);

  const parentTypeList = useMemo(
    () => [...locationTypes, ...hierarchyLocationTypes],
    [locationTypes, hierarchyLocationTypes],
  );

  const areaDepotNameMap = useMemo(() => {
    if (!isAreaParentPickerLabel(parentLocationTypeLabel)) return new Map();
    return buildAreaDepotNameMap(projectLocationIndex, parentTypeList);
  }, [projectLocationIndex, parentLocationTypeLabel, parentTypeList]);

  const parentSelectOptions = useMemo(() => {
    if (parentLocationTypeId == null) return [];

    const typeList = parentTypeList;
    const indexRows = projectLocationIndex;
    const byId = new Map();
    for (const row of indexRows) {
      byId.set(Number(row.id), row);
    }

    const typeMap = buildLocationTypeMetaMap(typeList);

    const choices = indexedParentChoices
      .map((raw) => mergeRowWithIndex(resolveParentChoiceRow(raw, byId), byId))
      .filter((r) => r.id != null);

    const enriched = enrichLocationsWithHierarchy(choices, { locationTypes: typeList }, indexRows);

    const parentTypeForLabel = parentLocationTypeMeta ?? {
      level: null,
      type_name: parentLocationTypeLabel,
      label: parentLocationTypeLabel,
    };

    return enriched.map((row) => {
      const merged = mergeRowWithIndex(row, byId);
      const deptOverride = areaDepotNameMap.get(Number(merged.id));
      const { primary, suffix } = buildParentSelectOptionParts(
        merged,
        parentTypeForLabel,
        parentLocationTypeLabel,
        byId,
        typeMap,
        deptOverride,
      );
      return {
        value: String(merged.id),
        primary,
        suffix,
        label: suffix ? `${primary}${suffix}` : primary,
        searchText: formatParentSelectOptionSearchText(merged),
      };
    });
  }, [
    parentLocationTypeId,
    parentLocationTypeMeta,
    parentLocationTypeLabel,
    indexedParentChoices,
    projectLocationIndex,
    areaDepotNameMap,
    parentTypeList,
  ]);

  const parentPlaceholder = useMemo(() => {
    if (parentLocationTypeId == null) {
      return selectedLocationTypeLabel ? 'None (root level)' : 'Select location type first';
    }
    if (projectFormIndexLoading && parentSelectOptions.length === 0) {
      return 'Loading parent locations…';
    }
    if (parentSelectOptions.length === 0) {
      return parentLocationTypeLabel
        ? `No ${parentLocationTypeLabel.toLowerCase()} locations found`
        : 'No parent locations found';
    }
    return parentLocationTypeLabel
      ? `Select ${parentLocationTypeLabel.toLowerCase()}…`
      : 'Select parent…';
  }, [
    parentLocationTypeId,
    projectFormIndexLoading,
    parentSelectOptions.length,
    selectedLocationTypeLabel,
    parentLocationTypeLabel,
  ]);

  const openModal = () => {
    setEditingLocationId(null);
    resetForm();
    setShowCreateModal(true);
  };

  useEffect(() => {
    if (!showCreateModal || form.project_id || projectOptions.length === 0) return;
    applyDefaultFormProject(formClientId);
  }, [
    showCreateModal,
    form.project_id,
    projectOptions,
    formClientId,
    applyDefaultFormProject,
  ]);

  const closeCreateModal = useCallback(() => {
    setSubmitting(false);
    setEditingLocationId(null);
    setShowCreateModal(false);
  }, []);

  const handleCreateLocation = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    try {
      const projectId = Number(form.project_id);
      const locationTypeId = Number(form.location_type_id);
      if (!Number.isInteger(projectId) || projectId <= 0) {
        throw new Error('Select a valid project.');
      }
      if (!Number.isInteger(locationTypeId) || locationTypeId <= 0) {
        throw new Error('Select a valid location type.');
      }

      const expectedParentTypeId = getParentLocationTypeId(locationTypes, locationTypeId);

      if (!form.name.trim()) {
        throw new Error('Enter a location name.');
      }

      const payload = {
        project_id: projectId,
        location_type_id: locationTypeId,
        name: form.name.trim(),
        code: form.code.trim() || null,
        address: form.address.trim() || null,
        pincode: form.pincode.trim() || null,
      };

      const latStr = String(form.latitude).trim();
      const lngStr = String(form.longitude).trim();
      if (latStr || lngStr) {
        const lat = Number(latStr);
        const lng = Number(lngStr);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          throw new Error('Enter valid latitude and longitude, or leave both empty.');
        }
        payload.latitude = lat;
        payload.longitude = lng;
      }

      const parentRaw = String(form.parent_id).trim();
      if (expectedParentTypeId != null) {
        if (parentRaw === '') {
          throw new Error('Select a parent location for this location type.');
        }
        const parentId = Number(parentRaw);
        if (!Number.isInteger(parentId) || parentId <= 0) {
          throw new Error('Parent location must be a valid selection.');
        }
        const parentValid = parentSelectOptions.some(
          (opt) => Number(opt.value) === parentId,
        );
        if (!parentValid) {
          throw new Error('Parent location does not match the hierarchy for this type.');
        }
        payload.parent_id = parentId;
      } else {
        payload.parent_id = null;
      }

      if (editingLocationId != null) {
        await updateLocation(editingLocationId, payload);
        toast.success('Location updated successfully.');
        closeCreateModal();
      } else {
        await createLocation(payload);
        toast.success('Location created successfully.');
        resetFormAfterCreate();
      }
      void loadData({ silent: true });
      const refreshProjectId = Number(form.project_id) || Number(scope.selectedProjectId);
      if (Number.isInteger(refreshProjectId) && refreshProjectId > 0) {
        const requestId = projectFormIndexRequestRef.current + 1;
        projectFormIndexRequestRef.current = requestId;
        await loadProjectFormIndex(refreshProjectId, {
          types: locationTypes,
          requestId,
          hasSeed: true,
        });
      }
    } catch (err) {
      const message = humanizeError(
        err,
        editingLocationId != null
          ? 'Could not update the location. Please try again.'
          : 'Could not create the location. Please try again.',
      );
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const assignBulkExcelFile = (file) => {
    if (!file) {
      setBulkFile(null);
      setBulkError('');
      return;
    }
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.xls')) {
      setBulkError('Please use an Excel file (.xlsx or .xls).');
      setBulkFile(null);
      if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
      return;
    }
    setBulkFile(file);
    setBulkError('');
    setBulkSuccess('');
  };

  const handleBulkFileChange = (event) => {
    const file = event.target.files?.[0];
    assignBulkExcelFile(file || null);
  };

  const openBulkModal = () => {
    setBulkError('');
    setBulkSuccess('');
    setBulkFile(null);
    if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
    if (hasClients && clientOptions.length > 0 && bulkClientId === '') {
      setBulkClientId(String(clientOptions[0].id));
    }
    setShowBulkModal(true);
  };

  const handleBulkUpload = async () => {
    setBulkUploading(true);
    setBulkError('');
    setBulkSuccess('');
    try {
      const projectId = Number(bulkProjectId);
      if (!Number.isInteger(projectId) || projectId <= 0) {
        throw new Error('Select a project for bulk upload.');
      }
      if (!bulkFile) {
        throw new Error('Choose a file to upload.');
      }

      const lower = bulkFile.name.toLowerCase();
      if (!lower.endsWith('.xlsx') && !lower.endsWith('.xls')) {
        throw new Error('Use an Excel file (.xlsx or .xls).');
      }

      const result = await uploadLocationsBulk(projectId, bulkFile);
      const imported =
        result?.data?.inserted ??
        result?.inserted ??
        result?.imported ??
        result?.count;
      const summary =
        (typeof result?.message === 'string' && result.message) ||
        (imported != null && Number.isFinite(Number(imported))
          ? `Imported ${imported} row(s).`
          : '') ||
        'Upload completed.';
      setBulkSuccess(summary);
      setBulkFile(null);
      if (bulkFileInputRef.current) {
        bulkFileInputRef.current.value = '';
      }
      await loadData({ silent: true });
      setTimeout(() => {
        setShowBulkModal(false);
        setBulkSuccess('');
      }, 900);
    } catch (err) {
      setBulkError(humanizeError(err, 'Bulk upload failed. Please verify the file and try again.'));
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <div className="fieldOfficerPage dashboard status-page">
      <header className="dashboard-page-header status-header-row">
        <div>
          <h2 className="dashboard-page-title">Locations</h2>
        </div>
        <div className="dashboard-header-toolbar">
          <ClientProjectDropdowns
            clientOptions={scope.clientDropdownOptions}
            projectOptions={scope.projectDropdownOptions}
            selectedClientId={scope.selectedClientId}
            selectedProjectId={scope.selectedProjectId}
            onClientChange={scope.onClientChange}
            onProjectChange={scope.onProjectChange}
            projectDisabled={scope.projectNeedsClientFirst || scope.noProjectsForClient}
            projectPlaceholder={scope.projectPlaceholder}
          />
          <button className="foPrimaryBtn foHeaderAddBtn" type="button" onClick={openModal}>
            + Add Location
          </button>
          <button className="foHeaderImportBtn" type="button" onClick={openBulkModal}>
            <FiUpload aria-hidden="true" />
            Import Excel
          </button>
        </div>
      </header>

      <div className="status-toolbar">
        <input
          className="status-search"
          type="search"
          placeholder="Search state, depot, area, qtr, name…"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          aria-label="Search by Keywords"
        />
        <div className="status-toolbar-right dashboard-header-toolbar">
          <HierarchyFilterDropdowns
            filterByOptions={filterByDropdownOptions}
            locationOptions={locationDropdownOptions}
            filterByField={filterByField}
            locationFilter={locationFilter}
            onFilterByChange={(next) => {
              setFilterByField(next ?? '');
              setLocationFilter('');
            }}
            onLocationChange={setLocationFilter}
            locationPlaceholder={activeFilterMeta.allLabel}
          />
          <Button type="button" className="export-meter-csv-button" onClick={exportCsv}>
            <img className="export-meter-csv-button-icon" src={exportIcon} alt="" aria-hidden="true" />
            <span className="export-meter-csv-button-label">Export</span>
          </Button>
        </div>
      </div>

      {pageFlash ? (
        <div className="locPageFlash" role="status">
          {pageFlash}
        </div>
      ) : null}

      <div className="status-table-wrap">
        {loading ? (
          <div className="status-state">Loading locations…</div>
        ) : error ? (
          <div className="status-state status-error">
            <p>{error}</p>
            <button type="button" className="foChipBtn" onClick={() => loadData()}>
              Retry
            </button>
          </div>
        ) : (
          <>
            <DataTable
              tableClassName="status-table locHierarchyTable"
              columns={locColumns}
              rows={pageRows}
              getRowKey={(row) => `loc-${row.id}`}
              emptyMessage="No locations found"
              emptyCellClassName="status-empty"
              aria-label="Locations"
            />

            <div className="foPagination">
              <span className="foTotalCount" aria-live="polite">
                Total: {displayRows.length} location{displayRows.length === 1 ? '' : 's'}
              </span>
              <div className="foPaginationControls">
                <button
                  className="foPageBtn"
                  type="button"
                  disabled={safePage === 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  aria-label="Previous page"
                >
                  <FiChevronLeft />
                </button>
                {pageList.map((entry, idx) => {
                  if (entry === 'left-ellipsis' || entry === 'right-ellipsis') {
                    return (
                      <span key={`${entry}-${idx}`} className="foPageEllipsis">
                        …
                      </span>
                    );
                  }
                  return (
                    <button
                      key={entry}
                      type="button"
                      className={`foPageNumber ${entry === safePage ? 'active' : ''}`}
                      onClick={() => setPage(entry)}
                    >
                      {entry}
                    </button>
                  );
                })}
                <button
                  className="foPageBtn"
                  type="button"
                  disabled={safePage === totalPages}
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  aria-label="Next page"
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showBulkModal && (
        <div
          className="foModalBackdrop"
          role="presentation"
          onClick={() => setShowBulkModal(false)}
        >
          <div
            className="foModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="loc-bulk-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="foModalHeader">
              <div>
                <h3 id="loc-bulk-modal-title">Import from Excel</h3>
                <p>Upload an .xlsx or .xls file for the selected project.</p>
              </div>
              <button type="button" className="foModalClose" onClick={() => setShowBulkModal(false)}>
                <FiX />
              </button>
            </div>

            <form
              className="foModalForm"
              onSubmit={(e) => {
                e.preventDefault();
                handleBulkUpload();
              }}
            >
              {hasClients && clientOptions.length > 0 ? (
                <label className="foSpanFull">
                  Client
                  <select
                    value={bulkClientId}
                    onChange={(e) => {
                      setBulkClientId(e.target.value);
                      setBulkProjectId('');
                    }}
                  >
                    {clientOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <label className="foSpanFull">
                Project
                <select
                  value={bulkProjectId}
                  onChange={(e) => setBulkProjectId(e.target.value)}
                >
                  <option value="">Select project…</option>
                  {bulkProjectOptions.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>

              <div className="foSpanFull locBulkFileField">
                <span className="locBulkFileLabel">Excel file</span>
                <div className="locBulkFileRow">
                  <input
                    ref={bulkFileInputRef}
                    type="file"
                    className="locBulkFileInput"
                    accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleBulkFileChange}
                  />
                  <button
                    type="button"
                    className="foChipBtn"
                    onClick={() => bulkFileInputRef.current?.click()}
                  >
                    Choose file
                  </button>
                  <span className="locBulkFileName" title={bulkFile?.name || ''}>
                    {bulkFile ? bulkFile.name : 'No file chosen'}
                  </span>
                </div>
              </div>

              {bulkError ? <p className="foSubmitError">{bulkError}</p> : null}
              {bulkSuccess ? <p className="foSubmitSuccess">{bulkSuccess}</p> : null}

              <div className="foModalActions">
                <button type="button" className="foChipBtn" onClick={() => setShowBulkModal(false)}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="foPrimaryBtn"
                  disabled={
                    bulkUploading ||
                    !bulkProjectId ||
                    !bulkFile ||
                    (hasClients && clientOptions.length === 0)
                  }
                >
                  {bulkUploading ? 'Uploading…' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div
          className="foModalBackdrop"
          role="presentation"
          onClick={closeCreateModal}
        >
          <div
            className="foModal locCreateModal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="foModalHeader">
              <div>
                <h3>{editingLocationId != null ? 'Edit Location' : 'Create Location'}</h3>
                <p>
                  {editingLocationId != null
                    ? 'Update location value and hierarchy mapping'
                    : 'Add a zone, site, or other location for a project'}
                </p>
              </div>
              <button type="button" className="foModalClose" onClick={closeCreateModal} aria-label="Close">
                <FiX />
              </button>
            </div>

            <form className="foModalForm locCreateModalForm" onSubmit={handleCreateLocation}>
              {hasClients && clientOptions.length > 0 ? (
                <label className="foSpanFull">
                  Client
                  <select
                    value={formClientId}
                    onChange={(e) => {
                      const next = e.target.value;
                      setFormClientId(next);
                      const projects = getSortedProjectOptions(user, next || null);
                      // eslint-disable-next-line no-console
                      console.log('[EditLocation] client changed', {
                        nextClientId: next,
                        projectCount: projects.length,
                        projectIds: projects.map((p) => p.id),
                        keepProjectId: editingLocationId != null ? form.project_id : null,
                      });
                      // When editing, keep the location's project if it still belongs to the new client.
                      const keepExisting =
                        editingLocationId != null &&
                        form.project_id &&
                        projects.some((p) => p.id === String(form.project_id));
                      const projectId = keepExisting
                        ? String(form.project_id)
                        : resolveDefaultFormProjectId(scope.selectedProjectId, projects);
                      setForm((prev) => ({
                        ...prev,
                        project_id: projectId,
                        location_type_id: '',
                        parent_id: '',
                      }));
                    }}
                  >
                    {clientOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {hasClients && clientOptions.length === 0 ? (
                <p className="foSubmitError foSpanFull">
                  No clients are assigned to your account. Contact an administrator.
                </p>
              ) : null}

              <label className="foSpanFull">
                Project
                <select
                  value={form.project_id}
                  required
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      project_id: e.target.value,
                      location_type_id: '',
                      parent_id: '',
                    }))
                  }
                >
                  {projectOptions.length === 0 ? (
                    <option value="">No projects available</option>
                  ) : null}
                  {projectOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label} (ID {p.id})
                    </option>
                  ))}
                </select>
              </label>

              <label className="foSpanFull">
                Location type
                <select
                  value={form.location_type_id}
                  disabled={!form.project_id || locationTypes.length === 0}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      location_type_id: e.target.value,
                      parent_id: '',
                    }))
                  }
                >
                  <option value="">
                    {form.project_id
                      ? locationTypes.length === 0
                        ? 'No types for this project'
                        : 'Select type…'
                      : 'Select project first'}
                  </option>
                  {locationTypes.map((t) => {
                    const id = locationTypeId(t);
                    return (
                      <option key={id} value={String(id)}>
                        {t.label ?? t.type_name} (level {t.level})
                      </option>
                    );
                  })}
                </select>
              </label>

              <label className="foSpanFull locParentDropdownField">
                {parentLocationTypeLabel
                  ? `Parent location (${parentLocationTypeLabel})`
                  : 'Parent location'}
                <Dropdown
                  options={parentSelectOptions}
                  value={form.parent_id}
                  onChange={(next) =>
                    setForm((prev) => ({
                      ...prev,
                      parent_id: next == null || next === '' ? '' : String(next),
                    }))
                  }
                  placeholder={parentPlaceholder}
                  searchable={parentSelectOptions.length > 0}
                  searchPlaceholder={
                    parentLocationTypeLabel
                      ? `Search ${parentLocationTypeLabel.toLowerCase()}…`
                      : 'Search parent location…'
                  }
                  ariaLabel="Parent location"
                  variant="modal"
                  modalLarge
                  rootClassName="locParentDropdown"
                  menuMinWidth={520}
                  menuMaxWidth={720}
                  disabled={
                    !form.location_type_id ||
                    parentLocationTypeId == null ||
                    (projectFormIndexLoading && parentSelectOptions.length === 0)
                  }
                />
              </label>

              <label className="foSpanFull">
                Value
                <input
                  required
                  placeholder="Enter your qtrno"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              </label>

              {submitError ? <p className="foSubmitError">{submitError}</p> : null}

              <div className="foModalActions">
                <button type="button" className="foChipBtn" onClick={closeCreateModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="foPrimaryBtn"
                  disabled={submitting || (hasClients && clientOptions.length === 0)}
                >
                  {submitting
                    ? editingLocationId != null
                      ? 'Updating...'
                      : 'Creating...'
                    : editingLocationId != null
                      ? 'Update Location'
                      : 'Create Location'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
